import React, { useState, useEffect, useRef } from "react";
import {
  Card, CardBody, CardHeader, Container,
  Row, Col, Spinner, Badge, ListGroup, ListGroupItem, Button,
  Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Input, Label
} from "reactstrap";
import { Toast } from "primereact/toast";
import axios from "axios";
import { API_BASE_URL } from "../../constants";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { Calendar, Plus } from "lucide-react";
import moment from "moment";
import ReactQuill from "react-quill";
import 'react-quill/dist/quill.snow.css';

const ScheduledTaskList = () => {
  const toast = useRef(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(moment().format("YYYY-MM-DD"));
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterPriority, setFilterPriority] = useState("all");
  const [filterStaff, setFilterStaff] = useState("all");
  const [staffMembers, setStaffMembers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentTaskId, setCurrentTaskId] = useState(null);
  const [staffLoading, setStaffLoading] = useState(false);
  const [projectsLoading, setProjectsLoading] = useState(false);

  const [taskFormData, setTaskFormData] = useState({
    title: "",
    description: "",
    status: "not_started",
    priority: "medium",
    assigned_to: "",
    due_date_time: moment().format("YYYY-MM-DDTHH:mm"),
    project_id: ""
  });

  const breadcrumbItems = [
    { title: "Dashboard", link: "/" },
    { title: "Scheduled Tasks", link: "#!" },
  ];

  const priorityBadge = (priority) => {
    const priorityMap = {
      low: "success",
      medium: "primary",
      high: "warning",
      urgent: "danger",
    };
    const priorityLabels = {
      low: "Low",
      medium: "Medium",
      high: "High",
      urgent: "Urgent",
    };
    return (
      <Badge color={priorityMap[priority] || "primary"} className="text-uppercase">
        {priorityLabels[priority] || priority}
      </Badge>
    );
  };

  const statusBadge = (status) => {
    const statusMap = {
      not_started: "secondary",
      in_progress: "info",
      completed: "success",
      on_hold: "warning",
      cancelled: "danger"
    };
    const statusLabels = {
      not_started: "Not Started",
      in_progress: "In Progress",
      completed: "Completed",
      on_hold: "On Hold",
      cancelled: "Cancelled"
    };
    return (
      <Badge color={statusMap[status] || "secondary"} className="text-uppercase">
        {statusLabels[status] || status.replace("_", " ")}
      </Badge>
    );
  };

  const fetchTasks = React.useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/projects/task/list?due_date=${selectedDate}`,
        {
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        }
      );

      if (response.data.status === "success") {
        setTasks(response.data.data);
      } else {
        throw new Error(response.data.message || "Failed to fetch tasks");
      }
    } catch (error) {
      console.error("Error fetching tasks:", error);
      showToast("error", "Error", error.response?.data?.message || "Failed to fetch tasks");
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  const fetchStaffMembers = React.useCallback(async () => {
    setStaffLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/staffs/list`,
        {
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        }
      );

      if (response.data.status === "success") {
        setStaffMembers(response.data.data);
      } else {
        throw new Error(response.data.message || "Failed to fetch staff members");
      }
    } catch (error) {
      console.error("Error fetching staff members:", error);
      showToast("error", "Error", error.response?.data?.message || "Failed to fetch staff members");
    } finally {
      setStaffLoading(false);
    }
  }, []);

  const fetchProjects = React.useCallback(async () => {
    setStaffLoading(true); // Using setStaffLoading as in original code, though maybe should have its own loading state or reuse projectsLoading? Original code used setStaffLoading.
    // Actually original code sets setStaffLoading=true but finally sets setProjectsLoading=false. 
    // And there is setProjectsLoading state. I will fix this logic slightly to be consistent if I can, but strict adherence to original logic is safer unless it's a bug.
    // Original: setStaffLoading(true) ... finally setProjectsLoading(false).
    // Let's assume the user meant setProjectsLoading(true).
    setProjectsLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/projects/list`,
        {
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        }
      );

      if (response.data.status === "success") {
        setProjects(response.data.data);
      } else {
        throw new Error(response.data.message || "Failed to fetch projects");
      }
    } catch (error) {
      console.error("Error fetching project:", error);
      showToast("error", "Error", error.response?.data?.message || "Failed to fetch projects");
    } finally {
      setProjectsLoading(false);
    }
  }, []);

  const formatDateTime = (dateTime) => {
    return dateTime ? moment(dateTime).format("MMM D, YYYY h:mm A") : "Not set";
  };

  const formatDecimalTime = (decimalHours) => {
    if (!decimalHours) return "0h 0m";
    const hours = Math.floor(decimalHours);
    const minutes = Math.floor((decimalHours % 1) * 60);
    return `${hours}h ${minutes}m`;
  };

  const showToast = (severity, summary, detail) => {
    toast.current?.show({
      severity,
      summary,
      detail,
      life: 3000,
    });
  };

  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
  };

  const handleStatusFilterChange = (e) => {
    setFilterStatus(e.target.value);
  };

  const handlePriorityFilterChange = (e) => {
    setFilterPriority(e.target.value);
  };

  const handleStaffFilterChange = (e) => {
    setFilterStaff(e.target.value);
  };

  const handleTaskInputChange = (e) => {
    const { name, value } = e.target;
    setTaskFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleTaskDescriptionChange = (value) => {
    setTaskFormData(prev => ({
      ...prev,
      description: value
    }));
  };

  const resetTaskForm = () => {
    setTaskFormData({
      title: "",
      description: "",
      status: "not_started",
      priority: "medium",
      assigned_to: "",
      due_date_time: moment().format("YYYY-MM-DDTHH:mm"),
      project_id: ""
    });
    setIsEditing(false);
    setCurrentTaskId(null);
  };

  const handleAddTaskClick = () => {
    resetTaskForm();
    setTaskModalOpen(true);
  };

  const handleTaskSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...taskFormData,
        added_by: localStorage.getItem("user_id")
      };

      if (isEditing && currentTaskId) {
        // Update existing task
        await axios.post(
          `${API_BASE_URL}/projects/task/update/${currentTaskId}`,
          payload,
          {
            headers: { Authorization: "Bearer " + localStorage.getItem("token") },
          }
        );
        showToast("success", "Success", "Task updated successfully");
      } else {
        // Add new task
        await axios.post(
          `${API_BASE_URL}/projects/task/add`,
          payload,
          {
            headers: { Authorization: "Bearer " + localStorage.getItem("token") },
          }
        );
        showToast("success", "Success", "Task added successfully");
      }

      setTaskModalOpen(false);
      fetchTasks();
    } catch (error) {
      console.error("Error saving task:", error);
      showToast("error", "Error", error.response?.data?.message || "Failed to save task");
    } finally {
      setLoading(false);
    }
  };

  const handleEditTask = (task) => {
    setTaskFormData({
      title: task.title,
      description: task.description,
      status: task.status,
      priority: task.priority,
      assigned_to: task.assigned_to.toString(),
      due_date_time: moment(task.due_date_time).format("YYYY-MM-DDTHH:mm"),
      project_id: task.project_id || ""
    });
    console.log("Task Description:", taskFormData.description);
    setIsEditing(true);
    setCurrentTaskId(task.id);
    setTaskModalOpen(true);
  };

  const filteredTasks = tasks.filter(task => {
    const statusMatch = filterStatus === "all" || task.status === filterStatus;
    const priorityMatch = filterPriority === "all" || task.priority === filterPriority;
    const staffMatch = filterStaff === "all" || task.assigned_to === parseInt(filterStaff);
    return statusMatch && priorityMatch && staffMatch;
  });

  useEffect(() => {
    fetchTasks();
    fetchStaffMembers();
    fetchProjects();
  }, [fetchTasks, fetchStaffMembers, fetchProjects]);

  const getCardHeaderColor = (task) => {
    if (task.status === "completed") return "#e8f5e9"; // Light green
    if (moment(task.due_date_time).isBefore(moment()) && task.status !== "completed") return "#ffebee"; // Light red
    return "#e3f2fd"; // Light blue
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Scheduled Tasks" breadcrumbItems={breadcrumbItems} />

          {/* Header with Add Task button */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4 className="mb-0">Scheduled Tasks</h4>
            <Button color="primary" onClick={handleAddTaskClick}>
              <Plus size={16} className="me-1" /> Add Task
            </Button>
          </div>

          {/* Filter Section */}
          <Card className="mb-4 shadow-sm">
            <CardBody className="py-3">
              <Row>
                <Col md={3} className="mb-3 mb-md-0">
                  <div className="form-group">
                    <label className="d-flex align-items-center text-muted">
                      <Calendar size={16} className="me-2" /> Select Date
                    </label>
                    <input
                      type="date"
                      value={selectedDate}
                      onChange={handleDateChange}
                      className="form-control"
                    />
                  </div>
                </Col>
                <Col md={3} className="mb-3 mb-md-0">
                  <div className="form-group">
                    <label className="text-muted">Filter by Status</label>
                    <select
                      className="form-control"
                      value={filterStatus}
                      onChange={handleStatusFilterChange}
                    >
                      <option value="all">All Statuses</option>
                      <option value="not_started">Not Started</option>
                      <option value="in_progress">In Progress</option>
                      <option value="on_hold">On Hold</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                </Col>
                <Col md={3} className="mb-3 mb-md-0">
                  <div className="form-group">
                    <label className="text-muted">Filter by Priority</label>
                    <select
                      className="form-control"
                      value={filterPriority}
                      onChange={handlePriorityFilterChange}
                    >
                      <option value="all">All Priorities</option>
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="form-group">
                    <label className="text-muted">Filter by Staff</label>
                    <select
                      className="form-control"
                      value={filterStaff}
                      onChange={handleStaffFilterChange}
                    >
                      <option value="all">All Staff</option>
                      {staffMembers.map((staff) => (
                        <option key={staff.user_id} value={staff.user_id}>
                          {staff.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>

          {loading ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
              <p className="mt-2">Loading tasks...</p>
            </div>
          ) : filteredTasks.length === 0 ? (
            <div className="text-center py-5">
              <p className="text-muted">No tasks found matching your filters</p>
              <Button
                color="primary"
                outline
                size="sm"
                onClick={() => {
                  setFilterStatus("all");
                  setFilterPriority("all");
                  setFilterStaff("all");
                }}
              >
                Clear Filters
              </Button>
            </div>
          ) : (
            <Row>
              {filteredTasks.map((task) => {
                // Find the staff member details for this task
                const assignedStaff = staffMembers.find(staff => staff.user_id === task.assigned_to);
                const assignedToName = assignedStaff ? assignedStaff.name : "Unassigned";

                return (
                  <Col md={6} xl={4} key={task.id} className="mb-4">
                    <Card className="h-100 task-card shadow-sm border-0">
                      <CardHeader
                        style={{ backgroundColor: getCardHeaderColor(task) }}
                        className="d-flex justify-content-between align-items-center py-3"
                      >
                        <h6 className="mb-0 text-truncate fw-semibold">{task.title}</h6>
                        <div className="d-flex gap-2">
                          {priorityBadge(task.priority)}
                          {statusBadge(task.status)}
                        </div>
                      </CardHeader>
                      <CardBody className="d-flex flex-column">
                        <div className="mb-3" style={{ minHeight: '90px', maxHeight: '120px', overflow: 'auto' }}>
                          <div
                            className="text-muted small mb-2"
                            dangerouslySetInnerHTML={{ __html: task.description || "No description provided" }}
                          />
                        </div>

                        <ListGroup flush className="mb-3 flex-grow-1">
                          {task.project_name && (
                            <ListGroupItem className="px-0 py-1 d-flex justify-content-between">
                              <span className="text-muted">Project:</span>
                              <span>{task.project_name}</span>
                            </ListGroupItem>
                          )}
                          <ListGroupItem className="px-0 py-1 d-flex justify-content-between">
                            <span className="text-muted">Assigned To:</span>
                            <span>{assignedToName}</span>
                          </ListGroupItem>
                          <ListGroupItem className="px-0 py-1 d-flex justify-content-between">
                            <span className="text-muted">Due:</span>
                            <span className={moment(task.due_date_time).isBefore(moment()) && task.status !== "completed" ? "text-danger fw-semibold" : ""}>
                              {formatDateTime(task.due_date_time)}
                            </span>
                          </ListGroupItem>
                          {task.total_time_taken && (
                            <ListGroupItem className="px-0 py-1 d-flex justify-content-between">
                              <span className="text-muted">Time Taken:</span>
                              <span>{formatDecimalTime(task.total_time_taken)}</span>
                            </ListGroupItem>
                          )}
                        </ListGroup>

                        <div className="mt-auto">
                          <Button
                            color="primary"
                            size="sm"
                            outline
                            onClick={() => handleEditTask(task)}
                          >
                            Edit Task
                          </Button>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          )}
        </Container>
      </div>

      {/* Task Modal */}
      <Modal isOpen={taskModalOpen} toggle={() => setTaskModalOpen(!taskModalOpen)} size="lg">
        <ModalHeader toggle={() => setTaskModalOpen(!taskModalOpen)}>
          {isEditing ? "Edit Task" : "Add New Task"}
        </ModalHeader>
        <Form onSubmit={handleTaskSubmit}>
          <ModalBody>
            <FormGroup>
              <Label for="taskTitle">Title</Label>
              <Input
                id="taskTitle"
                name="title"
                value={taskFormData.title}
                onChange={handleTaskInputChange}
                required
              />
            </FormGroup>

            <FormGroup>
              <Label for="taskDescription">Description</Label>
              <ReactQuill
                theme="snow"
                value={taskFormData.description}
                onChange={handleTaskDescriptionChange}
                style={{ height: "150px" }}
                modules={{
                  toolbar: [
                    [{ 'header': [1, 2, false] }],
                    ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                    ['link', 'image'],
                    ['clean']
                  ]
                }}
              />
            </FormGroup>
            <br />
            <br />
            <br />
            <Row>
              <Col md={4}>
                <FormGroup>
                  <Label for="taskProject">Project</Label>
                  <Input
                    id="taskProject"
                    name="project_id"
                    type="select"
                    value={taskFormData.project_id}
                    onChange={handleTaskInputChange}
                    disabled={projectsLoading}
                  >
                    <option value="">Select Project</option>
                    {projects.map((project) => (
                      <option key={project.id} value={project.id}>
                        {project.name}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label for="taskStatus">Status</Label>
                  <Input
                    id="taskStatus"
                    name="status"
                    type="select"
                    value={taskFormData.status}
                    onChange={handleTaskInputChange}
                  >
                    <option value="not_started">Not Started</option>
                    <option value="in_progress">In Progress</option>
                    <option value="on_hold">On Hold</option>
                    <option value="completed">Completed</option>
                  </Input>
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label for="taskPriority">Priority</Label>
                  <Input
                    id="taskPriority"
                    name="priority"
                    type="select"
                    value={taskFormData.priority}
                    onChange={handleTaskInputChange}
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </Input>
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="taskAssignedTo">Assign To</Label>
                  <Input
                    id="taskAssignedTo"
                    name="assigned_to"
                    type="select"
                    value={taskFormData.assigned_to}
                    onChange={handleTaskInputChange}
                    disabled={staffLoading}
                  >
                    <option value="">Select Staff</option>
                    {staffMembers.map((staffMember) => (
                      <option key={staffMember.user_id} value={staffMember.user_id}>
                        {staffMember.name}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="taskDueDateTime">Due Date & Time</Label>
                  <Input
                    id="taskDueDateTime"
                    name="due_date_time"
                    type="datetime-local"
                    value={taskFormData.due_date_time}
                    onChange={handleTaskInputChange}
                  />
                </FormGroup>
              </Col>
            </Row>
          </ModalBody>

          <ModalFooter>
            <Button color="light" onClick={() => setTaskModalOpen(false)}>
              Cancel
            </Button>
            <Button color="primary" type="submit" disabled={loading}>
              {isEditing ? "Update" : "Add"} Task
              {loading && <Spinner size="sm" className="ms-2" />}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      <Toast ref={toast} position="top-right" />
    </React.Fragment>
  );
};

export default ScheduledTaskList;